import { motion, useReducedMotion } from "framer-motion";

/**
 * Locked activation content for the "Cómo empezar" landing section. Each card
 * now describes the list workflow (create → quote → optimize) and carries a
 * real GCS-hosted hero illustration. The image is decorative — the eyebrow,
 * title and description below carry the semantic meaning — so it ships with
 * `alt=""` + `aria-hidden="true"` and stays out of the accessible name.
 */
type Step = {
    readonly n: number;
    readonly eyebrow: string;
    readonly title: string;
    readonly description: string;
    readonly imageSrc: string;
};

const STEP_IMAGES_BASE = "https://storage.googleapis.com/carry_images/web/";

const STEPS: readonly Step[] = [
    {
        n: 1,
        eyebrow: "Paso 1",
        title: "Crea tu lista",
        description:
            "Indica qué productos necesitas y qué atributos son importantes para ti: tamaño, marca, condición alimentaria, etc.",
        imageSrc: `${STEP_IMAGES_BASE}how_to_step_one.webp`,
    },
    {
        n: 2,
        eyebrow: "Paso 2",
        title: "Cotiza tu lista",
        description:
            "Buscaremos qué productos satisfacen las características de lo que buscas y son los más económicos.",
        imageSrc: `${STEP_IMAGES_BASE}how_to_step_two.webp`,
    },
    {
        n: 3,
        eyebrow: "Paso 3",
        title: "Optimiza",
        description:
            "Maximizaremos tu ahorro encontrando el o los lugares donde comprar tu lista, obteniendo el total más bajo. Y ojo, creamos el carrito directo en el súper elegido para que no pierdas tiempo en eso.",
        imageSrc: `${STEP_IMAGES_BASE}how_to_step_three.webp`,
    },
];

/**
 * Landing activation section: a 3-step illustrative card grid ("how to start"),
 * visually distinct from the CarriSteps icon cards (those are lighter / tighter
 * and live higher on the page). Each card carries a real hero illustration
 * (rounded panel + GCS-hosted image) plus an eyebrow / title / description.
 */
export const HowToStart = () => {
    const reduceMotion = useReducedMotion();

    return (
        <motion.section
            aria-labelledby="how-to-start-heading"
            className="mt-16 lg:mt-24 mb-8 lg:mb-16"
            initial={reduceMotion ? false : { opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
        >
            <div className="flex flex-row items-center justify-center gap-2 text-primary">
                <span aria-hidden="true">•</span>
                <span className="uppercase tracking-wider text-sm font-semibold text-primary">
                    CÓMO EMPEZAR?
                </span>
            </div>
            <h2
                id="how-to-start-heading"
                className="text-3xl md:text-4xl xl:text-5xl font-bold tracking-tight text-center mt-4"
            >
                Solo te tomará 10 minutos comenzar a ahorrar
            </h2>
            <p className="text-base xl:text-lg text-muted-foreground text-center max-w-2xl mx-auto mt-4">
                Un proceso simple de tres pasos para empezar a ahorrar.
            </p>
            <ol className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mt-12 lg:mt-16">
                {STEPS.map((step, i) => (
                    <motion.li
                        key={step.title}
                        className="relative flex flex-col rounded-3xl bg-card border border-border overflow-hidden shadow-sm hover:shadow-md transition-shadow p-6 lg:p-8"
                        initial={reduceMotion ? false : { opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.08 * i }}
                    >
                        {/* Hero illustration zone — real GCS image, decorative. */}
                        <div className="aspect-[5/3] w-full rounded-2xl overflow-hidden bg-muted">
                            <img
                                src={step.imageSrc}
                                alt=""
                                aria-hidden="true"
                                loading="lazy"
                                decoding="async"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="mt-6 flex flex-col gap-3">
                            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
                                {step.eyebrow}
                            </span>
                            <h3 className="text-xl xl:text-2xl font-bold tracking-tight">
                                {step.title}
                            </h3>
                            <p className="text-sm xl:text-base text-muted-foreground leading-relaxed">
                                {step.description}
                            </p>
                        </div>
                    </motion.li>
                ))}
            </ol>
        </motion.section>
    );
};
